import React, { useState, useEffect, useRef, useContext } from "react";
import { DayPicker } from "react-day-picker";
import { ChevronDown, Calendar } from "react-feather";
import "react-day-picker/dist/style.css";
import styles from "./FilterModal.module.css";
import AppContext from "../../store/app-context";
import { enUS, fr, de, es, ja, it, ko, zhCN , pt} from 'date-fns/locale';
export const formatDate = (dateInput, locale = "en-US") => {
  if (!dateInput) return "";

  const date = new Date(dateInput); 

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return locale === "en-US"
    ? `${month}/${day}/${year}`
    : `${day}/${month}/${year}`;
};

const parseDateFromString = (str, locale = "en-US") => {
  const parts = str.split("/");
  if (parts.length !== 3) return null;

  let day, month, year;
  if (locale === "en-US") {
    [month, day, year] = parts;
  } else {
    [day, month, year] = parts;
  }

  const date = new Date(`${year}-${month}-${day}`);
  if (date.toString() === "Invalid Date") return null;
  return date;
};

export const getDefaultDateRange = () => {
  const today = new Date();
  const lastYear = new Date();
  lastYear.setFullYear(today.getFullYear() - 1);
  return { from: lastYear, to: today };
};

const FilterModal = ({ initialRange, initialStatus, onClose, onFilterChange, statusOptions = [], locale = "en-US" }) => {
  const [range, setRange] = useState(initialRange || {});
  const [status, setStatus] = useState(initialStatus || []);
  const [showDateSection, setShowDateSection] = useState(false);
  const [showStatusSection, setShowStatusSection] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [error, setError] = useState("");

  const calendarRef = useRef();
  const appCtx = useContext(AppContext);

  const [fromInput, setFromInput] = useState(formatDate(range?.from, locale));
  const [toInput, setToInput] = useState(formatDate(range?.to, locale));

  const [activeInput, setActiveInput] = useState("from");
  const [prevInput, setPrevInput] = useState("from");

  const localeMap = {
    en: enUS,
    fr: fr,
    de: de,
    es: es,
    ja: ja,
    it: it,
    ko: ko,
    pt: pt,
    zh: zhCN,
  };

  const normalizeLocale = (localeStr) => {
    if (!localeStr) return 'en';
    return localeStr.split('-')[0]; 
  };
  const userLocale = normalizeLocale(locale);

  let apply = 'Apply';
  let cancel = 'Cancel';
  let resetAll = 'Reset All';
  let quoteExpiration = 'Quote expiration';
  let selectDateMsg = 'Select a date range up to one year.';
  let dateFormat = 'mm/dd/yyyy';
  let filters = 'Filters';
  let To = 'To';
  let From = 'From';
  let quoteStatus = 'Quote status'

  if(appCtx && appCtx.bioradResources){
    apply = appCtx.bioradResources?.Apply ?? 'Apply';
    cancel = appCtx.bioradResources?.Cancel ?? 'Cancel';
    resetAll = appCtx.bioradResources?.ResetAll ?? 'Reset All';
    quoteExpiration = appCtx.bioradResources?.QuoteExpiration ?? 'Quote expiration';
    selectDateMsg = appCtx.bioradResources?.SelectDateRange ?? 'Select a date range up to one year.';
    dateFormat = appCtx.bioradResources?.DateFormat ?? 'mm/dd/yyyy';
    filters = appCtx.bioradResources?.QuoteFilter ?? 'Filters';
    To = appCtx.bioradResources?.To ?? 'To';
    From = appCtx.bioradResources?.From ?? 'From';
    quoteStatus = appCtx.bioradResources?.QuoteStatusFilter ?? 'Quote status'
 }

  useEffect(() => {
    setRange(initialRange || {});
    setStatus(initialStatus || []);
    setFromInput(formatDate(initialRange?.from, locale));
    setToInput(formatDate(initialRange?.to, locale));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [initialRange, initialStatus, locale]);

  const handleStatusChange = (val) => {
    setStatus((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleApply = () => {
    let adjustedRange = { ...range };
    if (adjustedRange.from && !adjustedRange.to) {
      adjustedRange.to = adjustedRange.from;
    } else if (!adjustedRange.from && adjustedRange.to) {
      adjustedRange.from = adjustedRange.to;
    }

    onFilterChange({ range: adjustedRange, status, reset: false });
    onClose();
  };

  const isValidDateString = (str) => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return false;
    return parseDateFromString(str, locale) !== null;
  };

  const validateDateRange = (from, to, selectedDate) => {
    const today = new Date();
    if (selectedDate > today) {
      return "Date cannot be in the future.";
    }
  
    if (from && to) {
      if (to < from) return "To date cannot be earlier than From date.";
  
      const diffInDays = Math.abs(to - from) / (1000 * 60 * 60 * 24);
      if (diffInDays > 366) {
        return "From and To dates must be within 1 year.";
      }
    }
  
    return "";
  };

  const handleInputChange = (e, type) => {
    const value = e.target.value;
    const isFrom = type === "from";

    if (isFrom) setFromInput(value);
    else setToInput(value);

    if (!isValidDateString(value)) {
      setError("Invalid date format");
      return;
    }

    const parsed = parseDateFromString(value, locale);
    if (!parsed || isNaN(parsed)) {
      setError("Invalid date");
      return;
    }

    const newFrom = type === "from" ? parsed : range.from;
    const newTo = type === "to" ? parsed : range.to;
    const validationError = validateDateRange(newFrom, newTo, parsed);
    if (validationError) {
      setError(validationError);
      return;
    }

    setRange((prev) => ({
      ...prev,
      [type]: parsed,
    }));

    setError("");
  };

  const handleCalendarSelect = (selectedDate) => {
    if (!selectedDate || isNaN(selectedDate)) return;
  
    const newFrom = activeInput === "from" ? selectedDate : range.from;
    const newTo = activeInput === "to" ? selectedDate : range.to;
  
    const validationError = validateDateRange(newFrom, newTo, selectedDate);
    if (validationError) {
      setError(validationError);
      return;
    }
  
    setRange((prev) => ({
      ...prev,
      [activeInput]: selectedDate,
    }));
  
    const formatted = formatDate(selectedDate, locale);
    if (activeInput === "from") setFromInput(formatted);
    else setToInput(formatted);
  
    setError("");
  };
  

  const handleReset = () => {
    const defaultRange = {};
    const emptyStatus = [];

    setRange(defaultRange);
    setFromInput(formatDate(defaultRange?.from, locale));
    setToInput(formatDate(defaultRange?.to, locale));
    setStatus(emptyStatus);
    onFilterChange({ range: defaultRange, status: emptyStatus, reset: true });
  };

  const modifiers = {
    start: range.from,
    end: range.to,
    range: { from: range.from, to: range.to },
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles["filter-modal"]}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>
          <div className={styles.header}>
            <div className={styles.heading}>
              <h2>{filters}</h2>
              <button
                className={`${styles.reset} ${styles.btnRounded}`}
                onClick={handleReset}
              >
                {resetAll}
              </button>
            </div>
            <button className={styles.close} onClick={onClose}>
              &times;
            </button>
          </div>
        </div>
        <div className={styles.filterForm}>
          <div className={styles.section}>
            <div
              className={styles.sectionHeader}
              onClick={() => setShowDateSection(!showDateSection)}
            >
              <h3>{quoteExpiration}</h3>
              <ChevronDown
                className={`${styles.chevron} ${showDateSection ? styles.rotate : ""}`}
              />
            </div>

            {showDateSection && (
              <>
                <p className={styles.dateDesc}>{selectDateMsg}</p>
                <div className={styles["date-inputs"]}>
                  {["from", "to"].map((field) => (
                    <div key={field} className={styles["date-box"]}>
                      <label>{field === "from" ? From : To}</label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          placeholder={dateFormat}
                          value={field === "from" ? fromInput : toInput}
                          onChange={(e) => handleInputChange(e, field)}
                          onFocus={() => setActiveInput(field)}
                        />
                        <div
                          className={styles.calendarIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveInput(field);
                            if(prevInput===field){
                            setShowCalendar((prev) => !prev);}
                            else{
                              setShowCalendar(true);
                            }
                            setPrevInput(field);
                          }}
                        >
                          <Calendar size={16} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {error && <p className={styles.error}>{error}</p>}
                {showCalendar && (
                  <div ref={calendarRef} className={styles.calendarContainer}>
                    <DayPicker
                      mode="single"
                      selected={range[activeInput]}
                      onSelect={handleCalendarSelect}
                      modifiers={modifiers}
                      modifiersClassNames={{
                        start: 'rdp-range_start',
                        end: 'rdp-range_end',
                        range: 'rdp-range',
                      }}
                      locale={localeMap[userLocale] || enUS}
                      disabled={{ after: new Date() }}
                      captionLayout="dropdown"
                      startMonth={new Date(2000, 0)}
                      endMonth={new Date(new Date().getFullYear(), 11)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {Array.isArray(statusOptions) && statusOptions.length > 0 && (
            <div className={styles.section}>
              <div
                className={styles.sectionHeader}
                onClick={() => setShowStatusSection(!showStatusSection)}
              >
                <h3>{quoteStatus}</h3>
                <ChevronDown
                  className={`${styles.chevron} ${
                    showStatusSection ? styles.rotate : ""
                  }`}
                />
              </div>

              {showStatusSection && (
                <div className={styles.checkboxGroup}>
                  {statusOptions.map(({ label, value }) => (
                    <label key={value} className={styles.statusOptions}>
                      <input
                        className={styles.statusCheckBox}
                        type="checkbox"
                        onChange={() => handleStatusChange(value)}
                        checked={status.includes(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.stickyFooter}>
          <button
            className={`${styles.btnRounded} ${styles.apply}`}
            onClick={handleApply}
            disabled={!!error}
          >
            {apply}
          </button>
          <button
            className={`${styles.btnRounded} ${styles.cancel}`}
            onClick={onClose}
          >
            {cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;